import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { X } from 'lucide-react';
import * as Dialog from '@radix-ui/react-dialog';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { AlertRule, AlertRuleFormValues } from '@/types/alerts';
import { DualRangeSlider } from '../ui/dual-range-slider';

// Schema for alert rule form
const alertRuleSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().nullable(),
  device_id: z.string().nullable(),
  sensor_type: z.string().nullable(),
  event_type: z.string().nullable(),
  condition_type: z.enum(['gt', 'lt', 'eq', 'range', 'between', 'outside']),
  threshold_value: z.union([z.number(), z.array(z.number()).length(2)]),
  duration_seconds: z.number().nullable(),
  cooldown_minutes: z.number().min(1),
  severity: z.enum(['critical', 'high', 'medium', 'low', 'info']),
  status: z.enum(['active', 'paused']),
});

interface AlertRuleFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: AlertRuleFormValues) => void;
  rule?: AlertRule | null;
  devices: { id: string; name: string }[];
  sensorTypes: string[];
  eventTypes: any[];
  isLoading?: boolean;
}

export const AlertRuleForm = ({
  isOpen,
  onClose,
  onSubmit,
  rule,
  devices,
  sensorTypes,
  eventTypes,
  isLoading = false,
}: AlertRuleFormProps) => {
  // For range thresholds
  const [isRangeCondition, setIsRangeCondition] = useState(false);
  const [minValue, setMinValue] = useState<number>(0);
  const [maxValue, setMaxValue] = useState<number>(100);

  // Initialize form with rule data if editing
  const form = useForm<AlertRuleFormValues>({
    resolver: zodResolver(alertRuleSchema),
    defaultValues: rule
      ? {
          ...rule,
          device_id: rule.device?.id || undefined,
          event_type: rule.notification_event_type?.code ?? null,
          // Convert threshold_value based on condition type
          threshold_value: Array.isArray(rule.threshold_value)
            ? rule.threshold_value
            : typeof rule.threshold_value === 'number'
              ? rule.threshold_value
              : 0,
        }
      : {
          name: '',
          device_id: devices.length > 0 ? devices[0].id : null,
          description: null,
          sensor_type: null,
          event_type: '',
          condition_type: 'gt',
          threshold_value: 0,
          duration_seconds: null,
          cooldown_minutes: 15,
          severity: 'medium',
          status: 'active',
        },
    values: rule
      ? {
          ...rule,
          device_id: rule.device?.id || undefined,
          event_type: rule.notification_event_type?.code ?? null,
          cooldown_minutes: rule.cooldown_minutes || 15,
          // Convert threshold_value based on condition type
          threshold_value: Array.isArray(rule.threshold_value)
            ? rule.threshold_value
            : typeof rule.threshold_value === 'number'
              ? rule.threshold_value
              : 0,
        }
      : {
          name: '',
          description: null,
          device_id: devices.length > 0 ? devices[0].id : null,
          sensor_type: null,
          event_type: '',
          condition_type: 'gt',
          threshold_value: 0,
          duration_seconds: null,
          cooldown_minutes: 15,
          severity: 'medium',
          status: 'active',
        },
  });

  // Update range condition state when condition type changes
  useEffect(() => {
    const conditionType = form.watch('condition_type');
    const isRange = ['range', 'between', 'outside'].includes(conditionType);
    setIsRangeCondition(isRange);

    // Initialize range values if needed
    if (isRange) {
      const currentValue = form.getValues('threshold_value');
      if (!Array.isArray(currentValue)) {
        form.setValue('threshold_value', [0, 100] as [number, number]);
        setMinValue(0);
        setMaxValue(100);
      } else {
        setMinValue(currentValue[0]);
        setMaxValue(currentValue[1]);
      }
    }
  }, [form.watch('condition_type'), form]);

  // Update form value when range sliders change
  useEffect(() => {
    if (isRangeCondition) {
      form.setValue('threshold_value', [minValue, maxValue] as [number, number]);
    }
  }, [minValue, maxValue, isRangeCondition, form]);

  const handleSubmit = async (data: AlertRuleFormValues) => {
    onSubmit(data);
    // onClose();
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content className="fixed left-[50%] top-[50%] max-h-[85vh] w-[90vw] max-w-[650px] translate-x-[-50%] translate-y-[-50%] rounded-lg bg-background p-6 shadow-lg focus:outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] overflow-auto">
          <div className="flex justify-between items-center mb-5">
            <Dialog.Title className="text-xl font-bold">{rule ? 'Edit Alert Rule' : 'Create Alert Rule'}</Dialog.Title>
            <Dialog.Close asChild>
              <Button variant="ghost" size="icon" className="rounded-full">
                <X className="h-4 w-4" />
              </Button>
            </Dialog.Close>
          </div>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Rule Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Temperature too high" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Alert when temperature exceeds normal range"
                        {...field}
                        value={field.value || ''}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="device_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Device</FormLabel>
                      <FormControl>
                        <Select
                          onValueChange={(value) => field.onChange(value === 'all' ? null : value)}
                          value={field.value?.toString() || 'all'}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="All Devices" />
                          </SelectTrigger>
                          <SelectContent>
                            {devices.map((device) => (
                              <SelectItem key={device.id} value={device.id}>
                                {device.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormDescription>Specific device or all devices for this rule</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="event_type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Event type</FormLabel>
                      <FormControl>
                        <Select
                          onValueChange={(value) => field.onChange(value || null)}
                          value={field.value ?? ''}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select event type" />
                          </SelectTrigger>
                          <SelectContent>
                            {eventTypes.map((type) => (
                              <SelectItem key={type.code} value={type.code}>
                                {type.code}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormDescription>Select notification event to trigger</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="sensor_type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Sensor Type</FormLabel>
                      <FormControl>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value || 'all'}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="All Sensors" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Sensors</SelectItem>
                            {sensorTypes.map((type) => (
                              <SelectItem key={type} value={type}>
                                {type}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormDescription>Type of sensor to monitor</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="condition_type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Condition Type</FormLabel>
                      <FormControl>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select condition" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="gt">Greater than</SelectItem>
                            <SelectItem value="lt">Less than</SelectItem>
                            <SelectItem value="eq">Equal to</SelectItem>
                            <SelectItem value="range">Within range</SelectItem>
                            <SelectItem value="outside">Outside range</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="threshold_value"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{isRangeCondition ? 'Threshold Range' : 'Threshold Value'}</FormLabel>
                      <FormControl>
                        {isRangeCondition ? (
                          <div className="space-y-4">
                            <div className="flex justify-between text-sm text-muted-foreground">
                              <span>Min: {minValue}</span>
                              <span>Max: {maxValue}</span>
                            </div>
                            <DualRangeSlider
                              value={[minValue, maxValue]}
                              min={0}
                              max={200}
                              step={1}
                              onValueChange={(values) => {
                                setMinValue(values[0]);
                                setMaxValue(values[1]);
                              }}
                            />
                          </div>
                        ) : (
                          <Input
                            type="number"
                            {...field}
                            onChange={(e) => field.onChange(parseFloat(e.target.value))}
                            value={(field.value as number) || 0}
                          />
                        )}
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="severity"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Alert Severity</FormLabel>
                      <FormControl>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select severity" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="critical">Critical</SelectItem>
                            <SelectItem value="high">High</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                            <SelectItem value="low">Low</SelectItem>
                            <SelectItem value="info">Info</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="cooldown_minutes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Cooldown Period (minutes)</FormLabel>
                      <FormControl>
                        <Input type="number" {...field} onChange={(e) => field.onChange(parseInt(e.target.value))} />
                      </FormControl>
                      <FormDescription>Minimum time between repeated alerts</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="duration_seconds"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Duration (seconds)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          placeholder="Optional"
                          {...field}
                          value={field.value || ''}
                          onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : null)}
                        />
                      </FormControl>
                      <FormDescription>Condition must persist for this duration</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center justify-between">
                        <FormLabel>Rule Status</FormLabel>
                        <FormControl>
                          <Switch
                            checked={field.value === 'active'}
                            onCheckedChange={(checked) => field.onChange(checked ? 'active' : 'paused')}
                          />
                        </FormControl>
                      </div>
                      <FormDescription>
                        {field.value === 'active' ? 'Rule is active' : 'Rule is paused'}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <Button variant="outline" type="button" onClick={onClose}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isLoading}>
                  {isLoading ? 'Saving...' : rule ? 'Update Rule' : 'Create Rule'}
                </Button>
              </div>
            </form>
          </Form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
